import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { Shield, Code2, Heart, Award, MapPin, Zap } from 'lucide-react';

export function About() {
  const values = [
    { icon: <Zap className="text-gold" />, title: 'Originalität', desc: 'Wir entwickeln Konzepte, die aus der Masse herausstechen. Keine Klischees, sondern echte Innovation.' },
    { icon: <Award className="text-gold" />, title: 'Premium Qualität', desc: 'Einmal zahlen, dauerhaft nutzen. Wir verzichten konsequent auf Abos und versteckte Kosten.' },
    { icon: <Shield className="text-gold" />, title: 'Privacy First', desc: 'Diskretion ist unser höchstes Gut. Kein Tracking, keine Cloud-Zwänge – Ihre Daten bleiben privat.' },
    { icon: <Code2 className="text-gold" />, title: 'Clean Code', desc: 'Professionelle Software-Architektur sorgt für schnelle Ladezeiten und höchste Stabilität.' },
    { icon: <Heart className="text-gold" />, title: 'Lifestyle Fokus', desc: 'Unsere Apps wie LustDuell sind darauf ausgelegt, Beziehungen auf seriöse Weise zu bereichern.' },
    { icon: <MapPin className="text-gold" />, title: 'Regionalität', desc: 'Ehrliche Entwicklung direkt aus Rostock. Wir sind greifbar und stehen hinter unseren Produkten.' },
  ];

  return (
    <>
      <SEO 
        title="Über JL-Software | Expertise & Vision" 
        description="Erfahren Sie mehr über JL-Software. Wir entwickeln seriöse Lifestyle-Apps und innovative Software-Lösungen mit Fokus auf Diskretion und Qualität."
        keywords="Softwareentwickler Rostock, App Entwicklung Paare, LustDuell Entwickler, JL-Software Philosophie"
        path="/ueber-uns" 
      />
      <div className="mt-[70px]">
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 text-center border-b border-white/[0.06] overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl lg:text-6xl font-display font-bold italic text-glow mb-6"
            >
              Digitale Erlebnisse mit <span className="text-gold">Anspruch</span>
            </motion.h1>
            <p className="text-text-secondary text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              JL-Software steht für die Verbindung von technischer Präzision und tiefem Verständnis für moderne Beziehungsdynamiken. Wir schaffen Software, die mehr ist als nur Code.
            </p>
          </div>
        </section>

        {/* Werte Grid */}
        <section className="max-w-[1200px] mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 rounded-3xl bg-surface border border-white/[0.06] hover:border-gold/20 transition-all group"
              >
                <div className="w-12 h-12 rounded-2xl bg-gold-dim flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-text-primary mb-3">{value.title}</h3>
                <p className="text-text-secondary leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Die Geschichte / Vision */}
        <section className="max-w-4xl mx-auto px-6 py-20 border-t border-white/[0.06]">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-3xl font-bold text-text-primary mb-8 text-center">Warum JL-Software?</h2>
            <div className="grid md:grid-cols-2 gap-12 text-text-secondary leading-relaxed">
              <p>
                In einer Welt voller austauschbarer Massenprodukte setzen wir auf Individualität. 
                Alles begann mit der Vision, Apps für den Lifestyle- und Erotikbereich zu entwickeln, 
                die nicht billig oder aufdringlich wirken, sondern durch Ästhetik und Tiefe überzeugen.
              </p>
              <p>
                Mit Projekten wie <strong className="text-gold">LustDuell</strong> haben wir bewiesen, 
                dass Gamification ein mächtiges Werkzeug sein kann, um Paare einander näherzubringen. 
                Dabei bleiben wir unseren Wurzeln treu: <strong className="text-text-primary">Made in Germany </strong> 
                ist für uns kein Slogan, sondern ein Qualitätsversprechen.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-surface-secondary/50 py-24 text-center border-t border-white/[0.06]">
          <div className="max-w-lg mx-auto px-6">
            <h2 className="text-3xl font-bold text-text-primary mb-6">Bereit für Neues?</h2>
            <p className="text-text-secondary mb-10">
              Entdecken Sie unsere Apps im Shop oder kontaktieren Sie uns für individuelle Projektanfragen.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/shop" className="px-8 py-4 bg-gold text-surface-primary font-bold rounded-xl shadow-gold hover:shadow-gold-lg hover:-translate-y-1 transition-all">
                Zum Shop
              </Link>
              <a href="mailto:JL-Software@gmx.de" className="px-8 py-4 border border-white/[0.1] text-text-primary font-bold rounded-xl hover:bg-white/5 transition-all">
                Kontakt aufnehmen
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}